import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RestService } from '@app/core/api/rest.service';
import {
  ArchivDocumentPersonCreate, ArchivDocumentPersonUpdate, Bankverbindung, DateString, KeyValue, PayloadArchivDocumentList, PayloadArchivDocumentPersonCreateList, PayloadArchivDocumentPersonList, PayloadArchivDocumentPersonPolizzeList, PayloadBankverbindungList, PayloadIEAusgangList, PayloadInteger, PayloadKundenverbund, PayloadLebenDaten, PayloadPerson, PayloadPersonBestandsdaten, PayloadPersonBezugspersonResultList, PayloadPersonCreate, PayloadPersonElektronischeKommunikationLogList, PayloadPersonGesundheitsfragebogenList, PayloadPersonGrosskunde, PayloadPersonGrosskundeBuchungResultList, PayloadPersonKontaktList, PayloadPersonKundenportalList, PayloadPersonLogList, PayloadPersonPolizzeResultList, PayloadPersonPraemien, PayloadPersonReferatList, PayloadPersonResultList, PayloadPersonRisikokaptial, PayloadPersonRolleList, PayloadPersonSchadenResultList, PayloadPersonSchadensatz, PayloadPersonSchElektronischeKommunikationLogList, PayloadPolizzeMailingList, PayloadPolizzeZahlung, PayloadPolizzeZahlungResultList, PayloadRisikoanalyse, PayloadRisikoklassifizierungList, PayloadSchadenFeedbackList, PayloadTerminList, PayloadTodoResult, PayloadTodoResultList, PayloadVerbundRisiko, PersonCreate, TodoCreation
} from '@pezi/entities';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RestPersonService extends RestService {

  constructor(protected http: HttpClient) {
    super(http);
  }

  searchPerson(searchParams: KeyValue<string, any>[]): Observable<PayloadPersonResultList> {
    const params = this.getKeyValueAsQueryParams(searchParams);
    const options = Object.assign({}, this.httpOptions,
      {params});
    return this.http
      .get<PayloadPersonResultList>(
        this.baseUrl + '/person/suche/person',
        options);
  }

  searchKunde(searchParams: KeyValue<string, any>[]): Observable<PayloadPersonResultList> {
    const params = this.getKeyValueAsQueryParams(searchParams);
    const options = Object.assign({}, this.httpOptions,
      {params});
    return this.http
      .get<PayloadPersonResultList>(
        this.baseUrl + '/person/suche/kunde',
        options);
  }

  searchBeitragszahler(searchParams: KeyValue<string, any>[]): Observable<PayloadPersonResultList> {
    const params = this.getKeyValueAsQueryParams(searchParams);
    const options = Object.assign({}, this.httpOptions,
      {params});
    return this.http
      .get<PayloadPersonResultList>(
        this.baseUrl + '/person/suche/beitragszahler',
        options);
  }

  public getPersonForId(personId: number): Observable<PayloadPerson> {
    return this.http
      .get<PayloadPerson>(this.baseUrl + '/person/' + personId, this.httpOptions);
  }

  public getPersonVersionForId(personId: number, versId: number): Observable<PayloadPerson> {
    return this.http
      .get<PayloadPerson>(this.baseUrl + '/person/' + personId + '/version/' + versId, this.httpOptions);
  }

  public getPoliceForPerson(personId: number): Observable<PayloadPersonPolizzeResultList> {
    return this.http
      .get<PayloadPersonPolizzeResultList>(this.baseUrl + '/person/' + personId + '/polizzen', this.httpOptions);
  }

  public getSchadenForPerson(personId: number): Observable<PayloadPersonSchadenResultList> {
    return this.http
      .get<PayloadPersonSchadenResultList>(this.baseUrl + '/person/' + personId + '/schaden', this.httpOptions);
  }

  public getSchadensatzForPerson(personId: number): Observable<PayloadPersonSchadensatz> {
    return this.http
      .get<PayloadPersonSchadensatz>(this.baseUrl + '/person/' + personId + '/schadensatz', this.httpOptions);
  }

  public getPersonRisikoanalyse(personId: number): Observable<PayloadRisikoanalyse> {
    return this.http
      .get<PayloadRisikoanalyse>(this.baseUrl + '/person/' + personId + '/risikoanalyse', this.httpOptions);
  }

  public getPersonBestandsdaten(personId: number): Observable<PayloadPersonBestandsdaten> {
    return this.http
      .get<PayloadPersonBestandsdaten>(this.baseUrl + '/person/' + personId + '/bestandsdaten', this.httpOptions);
  }

  public getLebenDatenForPerson(personId: number): Observable<PayloadLebenDaten> {
    return this.http
      .get<PayloadLebenDaten>(this.baseUrl + '/person/' + personId + '/lebendaten', this.httpOptions);
  }

  public getKontakteForPerson(personId: number): Observable<PayloadPersonKontaktList> {
    return this.http
      .get<PayloadPersonKontaktList>(this.baseUrl + '/person/' + personId + '/kontakte', this.httpOptions);
  }

  public getPerLogForPerson(personId: number): Observable<PayloadPersonLogList> {
    return this.http
      .get<PayloadPersonLogList>(this.baseUrl + '/person/' + personId + '/logs/perlog', this.httpOptions);
  }

  public getPersonDocuments(personId: number): Observable<PayloadArchivDocumentPersonList> {
    return this.http
      .get<PayloadArchivDocumentPersonList>(this.baseUrl + '/person/' + personId + '/document', this.httpOptions);
  }

  public getPersonDocumentCreateModel(personId: number): Observable<PayloadArchivDocumentPersonCreateList> {
    return this.http
      .get<PayloadArchivDocumentPersonCreateList>(this.baseUrl + '/person/' + personId + '/document/create', this.httpOptions);
  }

  public createPersonDocument(personId: number, body: ArchivDocumentPersonCreate): Observable<PayloadInteger> {
    return this.http
      .post<PayloadInteger>(
        this.baseUrl + '/person/' + personId + '/document/create',
        body,
        this.httpOptions);
  }

  public getPersonVollmachten(personId: number): Observable<PayloadArchivDocumentPersonList> {
    return this.http
      .get<PayloadArchivDocumentPersonList>(this.baseUrl + '/person/' + personId + '/vollmacht', this.httpOptions);
  }

  public getPersonVollmachtPolizzen(personId: number, betreuerId?: number): Observable<PayloadArchivDocumentPersonPolizzeList> {
    let params = new HttpParams();
    params = params.set('betreuerId', betreuerId);
    const options = Object.assign({}, this.httpOptions,
      {params});
    return this.http
      .get<PayloadArchivDocumentPersonPolizzeList>(this.baseUrl + '/person/' + personId + '/vollmacht/polizzen', betreuerId ? options : this.httpOptions);
  }

  public getPersonVollmachtCreateModel(personId: number): Observable<PayloadArchivDocumentPersonCreateList> {
    return this.http
      .get<PayloadArchivDocumentPersonCreateList>(this.baseUrl + '/person/' + personId + '/vollmacht/create', this.httpOptions);
  }

  public createPersonVollmacht(personId: number, body: ArchivDocumentPersonCreate): Observable<PayloadInteger> {
    return this.http
      .post<PayloadInteger>(
        this.baseUrl + '/person/' + personId + '/vollmacht/create',
        body,
        this.httpOptions);
  }

  public updatePersonDocument(personId: number, personDocumentId: number, body: ArchivDocumentPersonUpdate): Observable<PayloadInteger> {
    return this.http
      .put<PayloadInteger>(
        this.baseUrl + '/person/' + personId + '/document/update/' + personDocumentId,
        body,
        this.httpOptions);
  }

  public updatePersonVollmacht(personId: number, personVollmachtId: number, body: ArchivDocumentPersonUpdate): Observable<PayloadInteger> {
    return this.http
      .put<PayloadInteger>(
        this.baseUrl + '/person/' + personId + '/vollmacht/update/' + personVollmachtId,
        body,
        this.httpOptions);
  }

  public searchPersonVollmachten(personId: number): Observable<PayloadArchivDocumentList> {
    return this.http
      .get<PayloadArchivDocumentList>(this.baseUrl + '/person/' + personId + '/vollmacht/create/search', this.httpOptions);
  }

  public getElekkommLogForPerson(personId: number): Observable<PayloadPersonElektronischeKommunikationLogList> {
    return this.http
      .get<PayloadPersonElektronischeKommunikationLogList>(
        this.baseUrl + '/person/' + personId + '/logs/elekkommlog', this.httpOptions);
  }

  public getSchElekkommLogForPerson(personId: number): Observable<PayloadPersonSchElektronischeKommunikationLogList> {
    return this.http
      .get<PayloadPersonSchElektronischeKommunikationLogList>(
        this.baseUrl + '/person/' + personId + '/logs/schelekkommlog', this.httpOptions);
  }

  public getMailingsForPerson(personId: number): Observable<PayloadPolizzeMailingList> {
    return this.http
      .get<PayloadPolizzeMailingList>(
        this.baseUrl + '/person/' + personId + '/mailings', this.httpOptions);
  }

  public getPraemienForPerson(personId: number): Observable<PayloadPersonPraemien> {
    return this.http
      .get<PayloadPersonPraemien>(
        this.baseUrl + '/person/' + personId + '/praemien', this.httpOptions);
  }

  public getPersonBankverbindungen(personId: number): Observable<PayloadBankverbindungList> {
    return this.http
      .get<PayloadBankverbindungList>(
        this.baseUrl + '/person/' + personId + '/bankverbindung', this.httpOptions);
  }

  public addPersonBankverbindung(personId: string, body: Bankverbindung): Observable<PayloadInteger> {
    return this.http
      .post<PayloadInteger>(
        this.baseUrl + '/person/' + personId + '/bankverbindung',
        body,
        this.httpOptions);
  }

  public getPersonCreateNat(): Observable<PayloadPersonCreate> {
    return this.http
      .get<PayloadPersonCreate>(
        this.baseUrl + '/person/create/nat', this.httpOptions);
  }

  public getPersonCreateJur(): Observable<PayloadPersonCreate> {
    return this.http
      .get<PayloadPersonCreate>(
        this.baseUrl + '/person/create/jur', this.httpOptions);
  }

  public getPersonForEdit(personId: string): Observable<PayloadPersonCreate> {
    return this.http
      .get<PayloadPersonCreate>(
        this.baseUrl + '/person/' + personId + '/edit', this.httpOptions);
  }

  public createPerson(person: PersonCreate): Observable<PayloadPersonCreate> {
    return this.http
      .put<PayloadPersonCreate>(
        this.baseUrl + '/person/create',
        person,
        this.httpOptions);
  }

  public getZahlungenForPerson(personId: number): Observable<PayloadPolizzeZahlungResultList> {
    return this.http
      .get<PayloadPolizzeZahlungResultList>(
        this.baseUrl + '/person/' + personId + '/zahlung', this.httpOptions);
  }

  public getPersonZahlungDetail(personId: number, zahlungId: number): Observable<PayloadPolizzeZahlung> {
    return this.http
      .get<PayloadPolizzeZahlung>(
        this.baseUrl + '/person/' + personId + '/zahlung/'+zahlungId, this.httpOptions);
  }

  public getKundenportalDaten(personId: number): Observable<PayloadPersonKundenportalList> {
    return this.http
      .get<PayloadPersonKundenportalList>(
        this.baseUrl + '/person/' + personId + '/kundenportal', this.httpOptions);
  }

  public getPersonVersionRollen(personId: number, version: number): Observable<PayloadPersonRolleList> {
    return this.http
      .get<PayloadPersonRolleList>(
        this.baseUrl + '/person/' + personId + '/version/' + version + '/rollen', this.httpOptions);
  }

  public getPersonVerbund(personId: number): Observable<PayloadKundenverbund> {
    return this.http
      .get<PayloadKundenverbund>(
        this.baseUrl + '/person/' + personId + '/verbund', this.httpOptions);
  }

  public getPersonVerbundSchadensatz(personId: number): Observable<PayloadPersonSchadensatz> {
    return this.http
      .get<PayloadPersonSchadensatz>(
        this.baseUrl + '/person/' + personId + '/verbund/schadensatz', this.httpOptions);
  }

  public getPersonVerbundRisiko(personId: number): Observable<PayloadVerbundRisiko> {
    return this.http
      .get<PayloadVerbundRisiko>(
        this.baseUrl + '/person/' + personId + '/verbund/risiko', this.httpOptions);
  }

  public getPersonReferate(personId: number): Observable<PayloadPersonReferatList> {
    return this.http
      .get<PayloadPersonReferatList>(
        this.baseUrl + '/person/' + personId + '/referate', this.httpOptions);
  }

  public getPersonTermine(personId: number): Observable<PayloadTerminList> {
    return this.http
      .get<PayloadTerminList>(
        this.baseUrl + '/person/' + personId + '/termine', this.httpOptions);
  }

  public getPersonInfoBrief(personId: number): Observable<PayloadPolizzeMailingList> {
    return this.http
      .get<PayloadPolizzeMailingList>(
        this.baseUrl + '/person/' + personId + '/mailings/infobrief', this.httpOptions);
  }

  public getPersonTodos(personId: number): Observable<PayloadTodoResultList> {
    return this.http
      .get<PayloadTodoResultList>(
        this.baseUrl + '/person/' + personId + '/todo', this.httpOptions);
  }

  public getPersonGrosskundenKonto(personId: number): Observable<PayloadPersonGrosskunde> {
    return this.http
      .get<PayloadPersonGrosskunde>(
        this.baseUrl + '/person/' + personId + '/grosskundenkonto', this.httpOptions);
  }

  public getPersonGrosskundenKontoBuchungen(personId: number, bbrvondatum?: DateString, bbrbisdatum?: DateString, polnrvon?: string, polnrbis?: string): Observable<PayloadPersonGrosskundeBuchungResultList> {
    const params = {bbrvondatum, bbrbisdatum, polnrvon, polnrbis};
    const options = Object.assign({}, this.httpOptions,
      {params});
    return this.http
      .get<PayloadPersonGrosskundeBuchungResultList>(
        this.baseUrl + '/person/' + personId + '/grosskundenkonto/buchungen', options);
  }

  public getPersonGrosskundenKontoLastschriften(personId: number): Observable<PayloadIEAusgangList> {
    return this.http
      .get<PayloadIEAusgangList>(
        this.baseUrl + '/person/' + personId + '/grosskundenkonto/lastschriften', this.httpOptions);
  }

  public getPersonGrosskundenBriefe(personId: number): Observable<PayloadArchivDocumentList> {
    return this.http
      .get<PayloadArchivDocumentList>(
        this.baseUrl + '/person/' + personId + '/briefe', this.httpOptions);
  }

  public getPersonPolizzenAufrecht(personId: number): Observable<PayloadPersonPolizzeResultList> {
    return this.http
      .get<PayloadPersonPolizzeResultList>(
        this.baseUrl + '/person/' + personId + '/polizzen/aufrecht', this.httpOptions);
  }

  public getPersonPolizzenAntragOffert(personId: number): Observable<PayloadPersonPolizzeResultList> {
    return this.http
      .get<PayloadPersonPolizzeResultList>(
        this.baseUrl + '/person/' + personId + '/polizzen/antragoffert', this.httpOptions);
  }

  public getPersonPolizzenStorniertErsetzt(personId: number): Observable<PayloadPersonPolizzeResultList> {
    return this.http
      .get<PayloadPersonPolizzeResultList>(
        this.baseUrl + '/person/' + personId + '/polizzen/storniertersetzt', this.httpOptions);
  }

  public getPersonRisikoklassifizierung(personId: number): Observable<PayloadRisikoklassifizierungList> {
    return this.http
      .get<PayloadRisikoklassifizierungList>(
        this.baseUrl + '/person/' + personId + '/risikoklassifizierung', this.httpOptions);
  }

  public getPersonGesundheitsfragebogenList(personId: number): Observable<PayloadPersonGesundheitsfragebogenList> {
    return this.http
      .get<PayloadPersonGesundheitsfragebogenList>(
        this.baseUrl + '/person/' + personId + '/gesundheitsfragebogen', this.httpOptions);
  }

  public printGesundheitsfragebogen(personId: number, gfbId: string, version: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get<Blob>(this.baseUrl + '/person/' + personId + '/gfb/' + gfbId + '/version/' + version + '/druck', {
        ...this.httpOptions,
        observe:      'response',
        responseType: 'blob' as 'json'
      });
  }

  public getPersonRisikokapital(personId: number, stichtag?: DateString): Observable<PayloadPersonRisikokaptial> {
    const params = {stichtag};
    const options = Object.assign({}, this.httpOptions,
      {params});
    return this.http
      .get<PayloadPersonRisikokaptial>(this.baseUrl + '/person/' + personId + '/risikokapital', options);
  }

  public getPersonBezugspersonen(personId: number): Observable<PayloadPersonBezugspersonResultList> {
    return this.http
      .get<PayloadPersonBezugspersonResultList>(
        this.baseUrl + '/person/' + personId + '/bezugspersonen', this.httpOptions);
  }

  public getPersonFeedbackSchaden(personId: number): Observable<PayloadSchadenFeedbackList> {
    return this.http
      .get<PayloadSchadenFeedbackList>(
        this.baseUrl +`/person/${personId}/feedback/schaden`, this.httpOptions);
  }

  public postPersonTodo(personId: number, body: TodoCreation): Observable<PayloadTodoResult> {
    return this.http
      .post<PayloadTodoResult>(
        this.baseUrl + '/person/' + personId + '/todo',
        body,
        this.httpOptions);
  }
}
